/**
 * Components for the business model strategy
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Chip, Paper, Grid, Divider } from '@mui/material';
import { COLORS } from '../../../config/colors';
import { COMPANY_STAGES, COMPANY_STAGE_CATEGORIES } from './constants';
import { getNextStrategy } from './strategy';
import { StrategyPhase } from '../baseStrategy';
import { setSelectedBusinessModels, fetchQueries } from '../../../utils/api';

/**
 * Renders business model options with multi-select capability
 * @param {Object} businessModelData - The business model data from API
 * @param {Function} onBusinessModelsSelected - Callback when user selects business models
 * @param {Function} advanceToNextTopic - Function to advance to the next topic
 * @param {Function} setMessages - Function to update messages state
 * @param {Function} handleRefreshResults - Function to refresh match results
 * @param {Function} onSendText - Function to send text message
 * @param {Array} messages - Current conversation messages
 * @returns {JSX.Element} The business model selection component
 */
export const BusinessModelOptions = ({
  businessModelData,
  onBusinessModelsSelected,
  advanceToNextTopic,
  setMessages,
  handleRefreshResults,
  onSendText,
  messages = []
}) => {
  // State for selected business models
  const [selectedModels, setSelectedModels] = useState([]);

  // Process business model data according to the expected structure
  // businessModelData should have:
  // - querySet: Object with category keys (CURRENT, RECENT, PAST) mapping to arrays of IDs
  // - queryDet: Object mapping IDs to details including name/label
  // - totalCount: Total count for percentage calculations
  // - Individual ID counts accessible via businessModelData[id]
  const modelsByCategory = {};

  // If we have the expected structure, use it
  if (businessModelData?.querySet && businessModelData?.queryDet) {
    // Process each category (CURRENT, RECENT, PAST)
    Object.entries(COMPANY_STAGE_CATEGORIES).forEach(([categoryKey, category]) => {
      // Get the IDs for this category
      const ids = businessModelData.querySet[categoryKey] || [];
      
      // Map IDs to model objects with details
      modelsByCategory[categoryKey] = ids.map(id => {
        const details = businessModelData.queryDet[id] || {};
        return {
          key: id,
          label: details.name || 'Unknown',
          category: categoryKey,
          value: details.id || '',
          count: businessModelData[id] || 0
        };
      });
    });
  }

  // Handle model selection/deselection
  const handleModelToggle = (modelKey) => {
    setSelectedModels(prev => {
      if (prev.includes(modelKey)) {
        return prev.filter(key => key !== modelKey);
      } else {
        return [...prev, modelKey];
      }
    });
  };

  // Handle confirm button click
  const handleConfirm = () => {
    if (selectedModels.length === 0) {
      return; // Don't proceed if no models selected
    }
    
    // Get the selected model details
    const selectedModelDetails = selectedModels.map(modelKey => {
      const model = COMPANY_STAGES[modelKey];
      return {
        key: modelKey,
        ...model
      };
    });
    
    // Store the selected models in the global state for API functions to use
    setSelectedBusinessModels(selectedModelDetails);
    console.log('Stored selected business models for API use:', selectedModelDetails);
    
    // Format the selected models for display in the message
    const formatSelectedModels = () => {
      const modelsByCategory = selectedModelDetails.reduce((acc, model) => {
        if (!acc[model.category]) {
          acc[model.category] = [];
        }
        acc[model.category].push(model.label);
        return acc;
      }, {});
      
      return Object.entries(modelsByCategory).map(([category, models]) => {
        const categoryLabel = COMPANY_STAGE_CATEGORIES[category].label;
        return `${categoryLabel}: ${models.join(', ')}`;
      }).join('; ');
    };
    
    // Add AI and user messages that explicitly summarize the selection
    if (setMessages) {
      // First add an AI message asking about business models
      const aiMessage = {
        role: 'AI',
        content: `What business models would you like to target for this role?`
      };
      
      // Then create the user message with a clear summary
      const userMessage = {
        role: 'User',
        content: `I'm looking for candidates with experience in the following business models: ${formatSelectedModels()}. This requirement is complete. Let's move on to the next topic.`
      };

      // Update messages state directly with both messages
      setMessages(prev => {
        const newMessages = [...prev, aiMessage, userMessage];
        return newMessages;
      });
      
      // Call handleRefreshResults to update the match results in the sidebar
      if (handleRefreshResults) {
        console.log('BusinessModelOptions: Calling handleRefreshResults to update match results');
        const allMessages = [...messages, aiMessage, userMessage];
        handleRefreshResults(allMessages);
      }
      
      // Directly call advanceToNextTopic if available to immediately move to the next topic
      if (advanceToNextTopic) {
        console.log('BusinessModelOptions: Directly calling advanceToNextTopic');
        setTimeout(() => {
          // Call advanceToNextTopic to update the discovery state
          const updatedDiscoveryState = advanceToNextTopic();

          // After updating the discovery state, use onSendText to trigger the state machine
          if (updatedDiscoveryState && updatedDiscoveryState.currentDiscoTopic) {
            const nextTopic = updatedDiscoveryState.currentDiscoTopic;
            
            // Use onSendText with a simple message to trigger the state machine
            if (onSendText) {
              // Create a message that will be recognized by the updated isSkipRequest in topicDecisionEngine
              const moveOnMessage = `I am done with my selections for Business Model and am ready to move on to the next topic.`;
              console.log(`BusinessModelOptions: Using onSendText with move-on message to trigger state machine transition to ${nextTopic}`);
              
              // Use a small delay to ensure the discovery state is updated first
              setTimeout(() => {
                onSendText(moveOnMessage);
              }, 200);
            } else {
              console.error('BusinessModelOptions: onSendText is not available, cannot trigger state machine');
            }
          }
        }, 100); // Small delay to ensure the messages are rendered first
      }
      
      // Call the callback with the selected models
      if (onBusinessModelsSelected) {
        onBusinessModelsSelected(selectedModelDetails);
      }
    }
  };

  // Get the total count from businessModelData
  const getTotalCount = () => {
    return businessModelData.totalCount || 0;
  };
  
  // Calculate percentage for a model
  const getModelPercentage = (modelKey) => {
    const count = businessModelData?.[modelKey] || 0;
    const total = getTotalCount();
    return total > 0 ? (count / total) * 100 : 0;
  };
  
  // Format percentage for display
  const formatPercentage = (percentage) => {
    if (percentage < 1 && percentage > 0) {
      return "<1%"; // Show "<1%" for very small percentages
    } else {
      return `${Math.round(percentage)}%`; // Round to nearest integer
    }
  };
  
  // Format count for display
  const formatCount = (count, modelKey) => {
    // Calculate percentage
    const percentage = getModelPercentage(modelKey);
    const percentageStr = formatPercentage(percentage);
    
    // Format the count
    let countStr;
    if (!count || count === 0) countStr = '0';
    else if (count < 1000) countStr = count.toString();
    else if (count < 10000) countStr = '1k+';
    else if (count < 100000) countStr = '10k+';
    else if (count < 1000000) countStr = '100k+';
    else countStr = '1M+';
    
    // Return both count and percentage
    return `${percentageStr}`;
  };

  return (
    <Box
      sx={{
        width: '100%',
        p: 4,
        borderRadius: 4,
        boxShadow: 3,
        bgcolor: 'white',
        overflow: 'hidden'
      }}
    >
      <Typography variant='h6' gutterBottom fontWeight='bold' color='#333'>
        Business Model Distribution
      </Typography>

      <Typography variant='body2' color='text.secondary' mb={2}>
        Below is the distribution of your existing workforce across different business models. 
        The percentages show what portion of your employees have worked with each model.
      </Typography>

      <Typography variant='body2' color='text.secondary' mb={3}>
        Select the business models you'd like to target for this role based on this data. 
        You can select multiple options to include candidates with experience in any of these models.
      </Typography>

      {/* Render each category section */}
      {Object.entries(modelsByCategory).map(([categoryKey, models], categoryIndex) => (
        <Box key={categoryKey} sx={{ mb: 4 }}>
          <Typography variant='subtitle1' fontWeight='bold' color='#555' mb={1}>
            {COMPANY_STAGE_CATEGORIES[categoryKey].label}
          </Typography>
          
          <Typography variant='body2' color='text.secondary' mb={2}>
            {categoryKey === 'CURRENT' 
              ? 'Employees currently working with these business models' 
              : categoryKey === 'RECENT' 
                ? 'Employees who recently worked with these business models'
                : 'Employees who previously worked with these business models'}
          </Typography>
          
          {/* Business model chips - horizontal row with wrapping */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            gap: 1, 
            mb: 2 
          }}>
            {models.map((model) => (
              <Chip
                key={model.key}
                label={`${model.label} (${formatCount(model.count, model.key)})`}
                variant={selectedModels.includes(model.key) ? 'filled' : 'outlined'}
                onClick={() => handleModelToggle(model.key)}
                sx={{
                  height: 32,
                  borderRadius: '16px',
                  bgcolor: selectedModels.includes(model.key) ? '#74A9F8' : 'transparent',
                  borderColor: '#74A9F8',
                  color: selectedModels.includes(model.key) ? 'white' : '#74A9F8',
                  '&:hover': { 
                    bgcolor: selectedModels.includes(model.key) ? '#5A93E5' : 'rgba(116, 169, 248, 0.1)'
                  },
                  '& .MuiChip-label': {
                    padding: '0 12px'
                  }
                }}
              />
            ))}
          </Box>
          
          {/* Add divider except for the last category */}
          {categoryIndex < Object.keys(modelsByCategory).length - 1 && (
            <Divider sx={{ my: 2 }} />
          )}
        </Box>
      ))}

      {/* Confirm button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          variant='contained'
          onClick={handleConfirm}
          disabled={selectedModels.length === 0}
          sx={{
            bgcolor: '#74A9F8',
            '&:hover': { bgcolor: '#5A93E5' },
            '&.Mui-disabled': {
              bgcolor: 'rgba(116, 169, 248, 0.3)',
              color: 'white'
            }
          }}
        >
          {selectedModels.length === 0 
            ? 'Select at least one option' 
            : `Confirm Selection (${selectedModels.length})`}
        </Button>
      </Box>
    </Box>
  );
};
